import type {
  LiveOhtRuntimeFrame,
  LiveOhtRuntimeSnapshot,
  LiveOhtTransferState,
} from '../types/amhsStream';
import { liveOhtRuntimeStore } from './liveOhtRuntimeStore';

/** Wall seconds to keep the full kit visible before / after the handoff animation. */
export const TRANSFER_EQUIPMENT_DETAIL_LEAD_S = 5;
export const TRANSFER_EQUIPMENT_DETAIL_TAIL_S = 5;

const PORT_OWNER_SUFFIX = /^(.*)-LP-\d+$/;

type Listener = () => void;

function stationKeysFromTransfer(transfer: LiveOhtTransferState): string[] {
  const keys = new Set<string>();
  if (transfer.stationId) keys.add(transfer.stationId);
  if (transfer.portId) {
    keys.add(transfer.portId);
    const owner = transfer.portId.match(PORT_OWNER_SUFFIX)?.[1];
    if (owner) keys.add(owner);
  }
  return [...keys];
}

function stationKeysFromRuntime(runtime: LiveOhtRuntimeSnapshot): string[] {
  const keys = new Set<string>();
  if (runtime.transfer) {
    for (const key of stationKeysFromTransfer(runtime.transfer)) keys.add(key);
  }
  if (runtime.gateTransfer) {
    keys.add(runtime.gateTransfer.equipmentId);
    keys.add(runtime.gateTransfer.portId);
    const owner = runtime.gateTransfer.portId.match(PORT_OWNER_SUFFIX)?.[1];
    if (owner) keys.add(owner);
  }
  return [...keys];
}

/**
 * Extends full-kit promotion around FOUP pick/drop so equipment does not pop
 * between instanced and animated forms exactly on transfer boundaries.
 */
class TransferEquipmentDetailHoldStore {
  private holdUntilMs = new Map<string, number>();
  private lastActiveKeys = new Set<string>();
  private listeners = new Set<Listener>();
  private version = 0;

  constructor() {
    liveOhtRuntimeStore.subscribe((frame) => {
      this.syncFrame(frame, performance.now());
    });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getVersion(): number {
    return this.version;
  }

  reset(): void {
    this.holdUntilMs.clear();
    this.lastActiveKeys.clear();
    this.bump();
  }

  isHeld(stationOrElementId: string, nowMs: number = performance.now()): boolean {
    const until = this.holdUntilMs.get(stationOrElementId);
    if (until != null && until > nowMs) return true;
    for (const [key, expiry] of this.holdUntilMs) {
      if (expiry <= nowMs) continue;
      if (stationOrElementId.startsWith(`${key}-`) || key.startsWith(`${stationOrElementId}-`)) {
        return true;
      }
    }
    return false;
  }

  syncFrame(frame: LiveOhtRuntimeFrame, nowMs: number): void {
    const currentlyActive = new Set<string>();
    let changed = false;

    for (const runtime of Object.values(frame.byOhtId)) {
      const keys = stationKeysFromRuntime(runtime);
      if (keys.length === 0) continue;

      const preTransfer =
        runtime.state === 'TRANSFER_WAIT' ||
        runtime.state === 'PICKING_UP' ||
        runtime.state === 'DROPPING';

      if (preTransfer) {
        for (const key of keys) {
          if (this.extendHold(key, nowMs + TRANSFER_EQUIPMENT_DETAIL_LEAD_S * 1000)) {
            changed = true;
          }
        }
      }

      const transfer = runtime.transfer;
      if (transfer) {
        for (const key of keys) currentlyActive.add(key);
        const remainingWallS = Math.max(
          0,
          transfer.durationSimS != null
            ? transfer.durationSimS - (transfer.elapsedSimS ?? 0)
            : (transfer.holdWallSec ?? 0) - (transfer.elapsedWallS ?? 0)
        );
        const until =
          nowMs + (remainingWallS + TRANSFER_EQUIPMENT_DETAIL_TAIL_S) * 1000;
        for (const key of keys) {
          if (this.extendHold(key, until)) changed = true;
        }
      }
    }

    for (const key of this.lastActiveKeys) {
      if (!currentlyActive.has(key)) {
        if (this.extendHold(key, nowMs + TRANSFER_EQUIPMENT_DETAIL_TAIL_S * 1000)) {
          changed = true;
        }
      }
    }
    this.lastActiveKeys = currentlyActive;

    for (const [key, until] of this.holdUntilMs) {
      if (until <= nowMs) {
        this.holdUntilMs.delete(key);
        changed = true;
      }
    }

    if (changed) this.bump();
  }

  private extendHold(key: string, untilMs: number): boolean {
    const prev = this.holdUntilMs.get(key) ?? 0;
    if (untilMs <= prev) return false;
    this.holdUntilMs.set(key, untilMs);
    return true;
  }

  private bump(): void {
    this.version += 1;
    for (const listener of this.listeners) listener();
  }
}

export const transferEquipmentDetailHoldStore = new TransferEquipmentDetailHoldStore();
